import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Card, CardContent } from "../components/ui/card";
import { 
  Image, Video, Plus, Trash2, X, Play, ChevronLeft, ChevronRight, 
  Calendar, MapPin, Users, ArrowLeft, Upload, ExternalLink 
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const { eventId } = useParams();
  const [galleryItems, setGalleryItems] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    media_type: "image",
    url: "",
    caption: "",
    thumbnail_url: ""
  });

  // Default placeholder images for each event
  const defaultGallery = {
    "hyderabad-2024": [
      { id: "h1", media_type: "image", url: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=800", caption: "Runners at the starting line - Gachibowli Stadium" },
      { id: "h2", media_type: "image", url: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800", caption: "Cyber awareness banners along the route" },
      { id: "h3", media_type: "image", url: "https://images.unsplash.com/photo-1513593771513-7b58b6c4af38?w=800", caption: "Finish line celebrations" },
      { id: "h4", media_type: "image", url: "https://images.unsplash.com/photo-1594882645126-14020914d58d?w=800", caption: "Government officials flagging off the run" },
      { id: "h5", media_type: "image", url: "https://images.unsplash.com/photo-1461897104016-0b3b00b4f9a9?w=800", caption: "Runners with FinRun t-shirts" },
      { id: "h6", media_type: "image", url: "https://images.unsplash.com/photo-1532444458054-01a7dd3e9fca?w=800", caption: "Medal ceremony" }
    ],
    "bengaluru-2025": [
      { id: "b1", media_type: "image", url: "https://images.unsplash.com/photo-1461897104016-0b3b00b4f9a9?w=800", caption: "St. Joseph's PU College grounds buzzing with energy" },
      { id: "b2", media_type: "image", url: "https://images.unsplash.com/photo-1532444458054-01a7dd3e9fca?w=800", caption: "Tech community participation" },
      { id: "b3", media_type: "image", url: "https://images.unsplash.com/photo-1594882645126-14020914d58d?w=800", caption: "Students spreading cyber awareness" },
      { id: "b4", media_type: "image", url: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=800", caption: "Corporate teams at the event" },
      { id: "b5", media_type: "image", url: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800", caption: "Prize distribution ceremony" },
      { id: "b6", media_type: "image", url: "https://images.unsplash.com/photo-1513593771513-7b58b6c4af38?w=800", caption: "Volunteers coordinating the event" }
    ]
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pastEventsRes, galleryRes] = await Promise.all([
          axios.get(`${API}/past-events`),
          axios.get(`${API}/gallery/${eventId}`)
        ]);
        
        setPastEvents(pastEventsRes.data.past_events);
        const event = pastEventsRes.data.past_events.find(e => e.id === eventId);
        setSelectedEvent(event);
        
        // Combine API gallery items with defaults
        const apiItems = galleryRes.data.items || [];
        const defaults = defaultGallery[eventId] || [];
        setGalleryItems(apiItems.length > 0 ? apiItems : defaults);
      } catch (e) {
        console.error("Error fetching gallery:", e);
        // Use defaults on error
        setGalleryItems(defaultGallery[eventId] || []);
        setSelectedEvent({
          id: eventId,
          name: eventId === "hyderabad-2024" ? "The FinRun Hyderabad 2024" : "The FinRun Bengaluru 2025",
          date: eventId === "hyderabad-2024" ? "December 2024" : "June 15, 2025",
          location: eventId === "hyderabad-2024" ? "Gachibowli Stadium, Hyderabad" : "St. Joseph's PU College, Bengaluru"
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [eventId]);

  const handleAddItem = async () => {
    if (!newItem.url) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      const response = await axios.post(`${API}/gallery`, {
        event_id: eventId,
        ...newItem
      });
      setGalleryItems(prev => [...prev, response.data]);
      setNewItem({ media_type: "image", url: "", caption: "", thumbnail_url: "" });
      setAddDialogOpen(false);
      toast.success("Media added to gallery!");
    } catch (error) {
      toast.error("Failed to add media");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/gallery/${itemId}`);
      setGalleryItems(prev => prev.filter(item => item.id !== itemId));
      toast.success("Media removed from gallery");
    } catch (error) {
      toast.error("Failed to remove media");
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % galleryItems.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  };

  const images = galleryItems.filter(item => item.media_type === "image");
  const videos = galleryItems.filter(item => item.media_type === "video");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#4B5563]">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="gallery-page">
      <Navbar />
      
      {/* Hero Banner */}
      <div className="bg-[#111827] pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src={galleryItems[0]?.url || "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=1200"}
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#111827]/80 to-[#111827]" />
        <div className="container-custom relative z-10">
          <Link 
            to="/#past-events" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Past Events
          </Link>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="text-[#FFD700] text-sm uppercase tracking-[0.2em] font-bold mb-2">
                {selectedEvent?.edition || "Event Gallery"}
              </p>
              <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 font-['Outfit']">
                {selectedEvent?.name || "Event Gallery"}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#FF3B30]" />
                  <span>{selectedEvent?.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#007AFF]" />
                  <span>{selectedEvent?.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#FFD700]" />
                  <span>{selectedEvent?.participants || "3,000+"} Participants</span>
                </div>
              </div>
            </div>
            
            {/* Add Media Button */}
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#FF3B30] hover:bg-[#FF1C0F] text-white rounded-full"
                  data-testid="add-media-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Media
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Media to Gallery</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Media Type</Label>
                    <Select 
                      value={newItem.media_type} 
                      onValueChange={(v) => setNewItem(prev => ({ ...prev, media_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="image">Image</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{newItem.media_type === "image" ? "Image URL" : "Video URL"} *</Label>
                    <Input 
                      placeholder={newItem.media_type === "image" ? "https://example.com/image.jpg" : "https://youtube.com/watch?v=..."}
                      value={newItem.url}
                      onChange={(e) => setNewItem(prev => ({ ...prev, url: e.target.value }))}
                    />
                    {newItem.media_type === "image" ? (
                      <div className="text-xs space-y-1">
                        <p className="text-[#4B5563]">
                          <strong>✅ Use:</strong> Direct image URLs ending in .jpg, .png, .webp
                        </p>
                        <p className="text-red-600">
                          <strong>❌ Won't work:</strong> Google Drive folders, webpage links
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-[#4B5563]">
                        Use YouTube, Vimeo links or direct MP4 URLs
                      </p>
                    )}
                  </div>
                  
                  {/* URL validation warning */}
                  {newItem.url && newItem.media_type === "image" && !newItem.url.match(/\.(jpg|jpeg|png|gif|webp|svg)($|\?)/i) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        ⚠️ This doesn't look like a direct image URL. 
                        <br />
                        <span className="text-yellow-700">Make sure it ends with .jpg, .png, .webp etc.</span>
                      </p>
                    </div>
                  )}
                  {newItem.media_type === "video" && (
                    <div className="space-y-2">
                      <Label>Thumbnail URL</Label>
                      <Input 
                        placeholder="https://example.com/thumbnail.jpg"
                        value={newItem.thumbnail_url}
                        onChange={(e) => setNewItem(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Caption</Label>
                    <Input 
                      placeholder="Describe this media..."
                      value={newItem.caption}
                      onChange={(e) => setNewItem(prev => ({ ...prev, caption: e.target.value }))}
                    />
                  </div>
                  <Button onClick={handleAddItem} className="w-full btn-primary">
                    <Upload className="w-4 h-4 mr-2" />
                    Add to Gallery
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="container-custom py-16">
        {/* Stats Bar */}
        <div className="flex flex-wrap gap-4 mb-12">
          <div className="bg-white rounded-full px-6 py-3 shadow-sm flex items-center gap-2">
            <Image className="w-5 h-5 text-[#FF3B30]" />
            <span className="font-semibold">{images.length} Photos</span>
          </div>
          <div className="bg-white rounded-full px-6 py-3 shadow-sm flex items-center gap-2">
            <Video className="w-5 h-5 text-[#007AFF]" />
            <span className="font-semibold">{videos.length} Videos</span>
          </div>
        </div>

        {/* Photos Section */}
        {images.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] flex items-center gap-3">
                <Image className="w-6 h-6 text-[#FF3B30]" />
                Photos
              </h2>
              {selectedEvent?.external_gallery_url && (
                <a 
                  href={selectedEvent.external_gallery_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF1C0F] text-white px-5 py-2.5 rounded-full font-semibold transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  View All Photos
                </a>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((item, index) => (
                <div 
                  key={item.id} 
                  className="relative group rounded-xl overflow-hidden aspect-square cursor-pointer"
                  onClick={() => openLightbox(galleryItems.indexOf(item))}
                >
                  <img 
                    src={item.url}
                    alt={item.caption || `Photo ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <ExternalLink className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {item.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-sm">{item.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* View All Photos CTA */}
            {selectedEvent?.external_gallery_url && (
              <div className="mt-8 text-center">
                <div className="inline-block bg-gradient-to-r from-[#FF3B30]/10 to-[#FFD700]/10 rounded-2xl p-8">
                  <p className="text-[#4B5563] mb-4">
                    Want to see more? Browse the complete gallery with {selectedEvent.id === "hyderabad-2024" ? "100+" : "200+"} photos!
                  </p>
                  <a 
                    href={selectedEvent.external_gallery_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF1C0F] text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg"
                  >
                    <Image className="w-5 h-5" />
                    View Complete Gallery
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Videos Section */}
        {videos.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] mb-6 flex items-center gap-3">
              <Video className="w-6 h-6 text-[#007AFF]" />
              Videos
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((item, index) => (
                <Card key={item.id} className="overflow-hidden">
                  <div className="relative aspect-video bg-[#111827]">
                    {item.thumbnail_url ? (
                      <img 
                        src={item.thumbnail_url}
                        alt={item.caption || `Video ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-16 h-16 text-gray-600" />
                      </div>
                    )}
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                    >
                      <div className="w-16 h-16 bg-[#FF3B30] rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-white ml-1" fill="white" />
                      </div>
                    </a>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-[#0A0A0A]">{item.caption || "Event Video"}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {galleryItems.length === 0 && (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-[#FAFAFA] rounded-full flex items-center justify-center mx-auto mb-6">
              <Image className="w-12 h-12 text-[#9CA3AF]" />
            </div>
            <h3 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] mb-4">
              No Media Yet
            </h3>
            <p className="text-[#4B5563] mb-6">
              Be the first to add photos and videos from this event!
            </p>
            <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
              <Plus className="w-5 h-5 mr-2" />
              Add Media
            </Button>
          </div>
        )}

        {/* Event Switcher */}
        <div className="mt-16 border-t pt-16">
          <h3 className="text-xl font-bold text-[#0A0A0A] font-['Outfit'] mb-6">
            Browse Other Events
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {pastEvents.filter(e => e.id !== eventId).map((event) => (
              <Link 
                key={event.id} 
                to={`/gallery/${event.id}`}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow flex items-center gap-4"
              >
                <div className="w-20 h-20 bg-[#FAFAFA] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Image className="w-8 h-8 text-[#4B5563]" />
                </div>
                <div>
                  <p className="text-sm text-[#FF3B30] font-medium">{event.edition}</p>
                  <p className="font-bold text-[#0A0A0A]">{event.name}</p>
                  <p className="text-sm text-[#4B5563]">{event.date} • {event.location}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          <button 
            onClick={() => setLightboxOpen(false)}
            className="absolute top-6 right-6 text-white hover:text-gray-300 z-10"
          >
            <X className="w-8 h-8" />
          </button>
          
          <button 
            onClick={prevImage}
            className="absolute left-6 text-white hover:text-gray-300"
          >
            <ChevronLeft className="w-12 h-12" />
          </button>
          
          <div className="max-w-5xl max-h-[80vh] px-16">
            {galleryItems[lightboxIndex]?.media_type === "image" ? (
              <img 
                src={galleryItems[lightboxIndex]?.url}
                alt={galleryItems[lightboxIndex]?.caption}
                className="max-w-full max-h-[80vh] object-contain"
              />
            ) : (
              <div className="text-center text-white">
                <Video className="w-24 h-24 mx-auto mb-4" />
                <a 
                  href={galleryItems[lightboxIndex]?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF3B30] hover:underline"
                >
                  Open Video in New Tab
                </a>
              </div>
            )}
            {galleryItems[lightboxIndex]?.caption && (
              <p className="text-white text-center mt-4">
                {galleryItems[lightboxIndex]?.caption}
              </p>
            )}
          </div>
          
          <button 
            onClick={nextImage}
            className="absolute right-6 text-white hover:text-gray-300"
          >
            <ChevronRight className="w-12 h-12" />
          </button>
          
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white">
            {lightboxIndex + 1} / {galleryItems.length}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
